/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { X, ImageIcon, Video as VideoIcon, Lock } from "lucide-react";
import Spinner from "../../misc/Spinner";
import type { MediaType } from "../../../types/mediaType";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../misc/Dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../misc/Tabs";
import { Button } from "../../misc/Button";
import { MediaPickerDialogProps } from "../types";
import { UnifiedMedia } from "../../listings/slideshow/types";
import PostMediaSelector from "./PostMediaSelector";
import { ResourceType } from "../../content/types";
import { PromotionType } from "../../promotions/types";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { SocialPlatforms } from "../../campaign/types";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import UploadNavigationWarning from "../../misc/UploadNavigationWarning";
import ConfirmPopup from "../../misc/ConfirmPopup";
import { cn } from "../../../utils/classMerger";
import {
    IMAGE_ONLY_PLATFORMS,
    VIDEO_ONLY_PLATFORMS,
    deriveSharedMediaSegment,
    segmentLockMessage,
    segmentSources,
    sharedSegmentCaption,
    sharedSegmentLocks,
    videoSideRequiresMedia,
    type SegmentSource,
    type SharedMediaSegment,
} from "./sharedMediaTypeControl";

const PLATFORM_LIMITS: Record<string, { min: number; max: number }> = {
    facebook: { min: 0, max: 10 },
    instagram: { min: 1, max: 10 },
    twitter: { min: 0, max: 4 },
    linkedin: { min: 0, max: 9 },
    pinterest: { min: 1, max: 1 },
    google: { min: 1, max: 1 },
    gmb: { min: 1, max: 1 },
};


type MediaDropdownType = "image" | "video" | "assets" | "promotionImages" | "archived";

const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
    isOpen,
    onClose,
    onNext,
    media,
    selectedMediaIds,
    setSelectedMediaIds,
    isMobile,
    platform,
    listingId,
    listingType,
    fetchMedia,
    uploadsEnabled = false,
    isVideo = false,
    isNext = false,
    postType,
    onMediaTypeChange,
    initialMediaType,
    isLoading = false,
    isSaving = false,
    sharedMediaOnly = false,
    mediaTypeSegments = false,
    showBackConfirm = false,
    onConfirmBack,
    onCancelBack,
    warnAboutContentEdits = false,
}) => {
    const [pendingUploads, setPendingUploads] = useState(0);
    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [activeUploads, setActiveUploads] = useState(0);
    const [showNavigationWarning, setShowNavigationWarning] = useState(false);
    const [showTabWarning, setShowTabWarning] = useState(false);
    const [pendingMediaType, setPendingMediaType] = useState<MediaDropdownType | null>(null);

    const platforms = useMemo(
        () => platform.split(",").map((p) => p.trim().toLowerCase()),
        [platform]
    );

    const limits = platforms.map(
        (p) => PLATFORM_LIMITS[p] || { min: 0, max: 10 }
    );

    // Video tab is hard-disabled when any selected platform is image-only.
    const isVideoDisabled = useMemo(() => {
        return (
            platform === SocialPlatforms.Pinterest ||
            platform === SocialPlatforms.Google ||
            platforms.some((p) => IMAGE_ONLY_PLATFORMS.includes(p))
        );
    }, [platform, platforms]);

    // Image tab is hard-disabled when any selected platform is video-only (e.g. YouTube),
    // or when the post itself is a video-only type (e.g. Video Showcase).
    const isImageDisabled = useMemo(() => {
        if (isVideo) return true; // video showcase is always video-only, regardless of platform
        return platforms.some((p) => VIDEO_ONLY_PLATFORMS.includes(p));
    }, [isVideo, platforms]);

    // Selection type checks for the segment derivation and the legacy-mixed
    // landing rule; the selection can span listing media, shared assets and
    // archived items.
    const selectionHasVideo = selectedMediaIds.some(
        (id) =>
            (media.videos ?? []).some((v) => v.id === id) ||
            (media.assets ?? []).some((a) => a.id === id && a.assetType === "Video")
    );
    const selectionHasImage = selectedMediaIds.some(
        (id) =>
            (media.images ?? []).some((i) => i.id === id) ||
            (media.assets ?? []).some((a) => a.id === id && a.assetType !== "Video") ||
            (media.archived ?? []).some((ar) => ar.id === id)
    );

    const sourceInput = {
        sharedMediaOnly,
        hasListingImages: (media.images ?? []).length > 0,
        hasListingVideos: (media.videos ?? []).length > 0,
        hasLogos: (media.assets ?? []).some((a) => a.assetType === "Logo"),
        hasArchived: (media.archived ?? []).length > 0,
    };
    // Last source the user chose on each side, so switching segments and
    // coming back restores their place (per mount).
    const lastSourceRef = useRef<{ images?: SegmentSource; video?: SegmentSource }>({});
    // Set once the user drives the control - the media-arrival re-derivation
    // must never fight an explicit choice.
    const userDroveSegmentsRef = useRef(false);
    // A segmented switch deferred behind the uploads-in-flight warning.
    const pendingSegmentApplyRef = useRef<(() => void) | null>(null);

    const defaultSourceFor = (segment: SharedMediaSegment): SegmentSource | null => {
        const remembered = lastSourceRef.current[segment];
        const sources = segmentSources(segment, sourceInput);
        if (remembered && sources.some((src) => src.key === remembered.key)) {
            return remembered;
        }
        return sources[0] ?? null;
    };

    const applySource = (
        source: SegmentSource | null,
        segment: SharedMediaSegment,
        remember = false
    ) => {
        if (!source) {
            // My Brand video side has no chips - the segment itself is the source.
            setMediaType("assets");
            setActiveResourceType(segment === "video" ? ResourceType.Video : ResourceType.Image);
            return;
        }
        if (remember) {
            lastSourceRef.current[segment] = source;
        }
        setMediaType(source.mediaType);
        setActiveResourceType(
            source.resourceType === "Video"
                ? ResourceType.Video
                : source.resourceType === "Logo"
                ? ResourceType.Logo
                : segment === "video"
                ? ResourceType.Video
                : ResourceType.Image
        );
    };

    const [mediaType, setMediaType] = useState<MediaDropdownType>(() => {
        if (sharedMediaOnly) return "assets";
        if (mediaTypeSegments) {
            const segment = deriveSharedMediaSegment({
                platforms,
                isVideo,
                hasVideoSelected: selectionHasVideo,
                hasImageSelected: selectionHasImage,
                priorChoice: initialMediaType,
            });
            return segmentSources(segment, sourceInput)[0]?.mediaType ?? "assets";
        }
        if (postType === "Promotion") {
            const archivedIds = new Set((media.archived ?? []).map((a) => a.id));
            if (selectedMediaIds.some((id) => archivedIds.has(id))) return "archived";
            const assetIds = new Set((media.assets ?? []).map((a) => a.id));
            if (selectedMediaIds.some((id) => assetIds.has(id))) return "assets";
            return "promotionImages";
        }
        if (isImageDisabled) return "video";
        if (isVideoDisabled) return "image";
        // A legacy mixed selection (pre-MB-1495) lands on the video side -
        // the video wins the flex lock, so that tab is the coherent one.
        if (selectionHasVideo && selectionHasImage) return "video";
        if (initialMediaType === "video") return "video";
        return isVideo ? "video" : "image";
    });

    // Media often arrives after the wizard's media step mounts; until the
    // user drives the control, follow the derivation as libraries appear so
    // the entry state reflects the real media, not the loading gap.
    useEffect(() => {
        if (!isOpen || !mediaTypeSegments || sharedMediaOnly) return;
        if (userDroveSegmentsRef.current) return;
        const segment = deriveSharedMediaSegment({
            platforms,
            isVideo,
            hasVideoSelected,
            hasImageSelected,
            priorChoice: initialMediaType,
        });
        applySource(defaultSourceFor(segment), segment, false);
    // deps are the library-presence booleans on purpose - reacting to anything
    // else here would fight explicit user choices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        sourceInput.hasListingImages,
        sourceInput.hasListingVideos,
        sourceInput.hasArchived,
    ]);

    // Sync mediaType whenever the dialog opens
    useEffect(() => {
        if (isOpen) {
            if (mediaTypeSegments) {
                if (sharedMediaOnly) setMediaType("assets");
                // Re-derive the segment on every entry: platform locks win,
                // an existing selection keeps its side, an explicit earlier
                // choice survives, and only then does the video-first
                // default apply - re-arming its caption when it does.
                const segment = deriveSharedMediaSegment({
                    platforms,
                    isVideo,
                    hasVideoSelected,
                    hasImageSelected,
                    priorChoice: initialMediaType,
                });
                if (sharedMediaOnly) {
                    setActiveResourceType(
                        segment === "video" ? ResourceType.Video : ResourceType.Image
                    );
                } else {
                    applySource(defaultSourceFor(segment), segment, false);
                }
                setHeuristicDefaultActive(
                    initialMediaType !== "image" &&
                        initialMediaType !== "video" &&
                        !hasVideoSelected &&
                        !hasImageSelected
                );
            } else if (sharedMediaOnly) {
                // Shared-media flows without the segment control (e.g. a
                // My Brand VIDEO post's edit dialog) still only have the
                // assets library.
                setMediaType("assets");
            } else if (postType === "Promotion") {
                const archivedIds = new Set((media.archived ?? []).map((a) => a.id));
                if (selectedMediaIds.some((id) => archivedIds.has(id))) {
                    setMediaType("archived");
                } else {
                    const assetIds = new Set((media.assets ?? []).map((a) => a.id));
                    setMediaType(selectedMediaIds.some((id) => assetIds.has(id)) ? "assets" : "promotionImages");
                }
            } else if (isImageDisabled) {
                setMediaType("video");
            } else if (isVideoDisabled) {
                setMediaType("image");
            } else if (selectionHasVideo && selectionHasImage) {
                setMediaType("video");
            } else if (initialMediaType === "video") {
                setMediaType("video");
            } else {
                setMediaType(isVideo ? "video" : "image");
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);


    const [activeResourceType, setActiveResourceType] =
        useState<ResourceType | null>(() => {
            if (mediaTypeSegments) {
                const segment = deriveSharedMediaSegment({
                    platforms,
                    isVideo,
                    hasVideoSelected: selectionHasVideo,
                    hasImageSelected: selectionHasImage,
                    priorChoice: initialMediaType,
                });
                const source = segmentSources(segment, sourceInput)[0];
                if (source?.resourceType === "Logo") return ResourceType.Logo;
                return segment === "video" ? ResourceType.Video : ResourceType.Image;
            }
            return isVideo ? ResourceType.Video : ResourceType.Image;
        });

    // True while the video-first default is what put the user on the Video
    // segment (no lock, no selection, no explicit earlier choice) - it gates
    // the "Video selected for you" caption and ends on the first segment click.
    const [heuristicDefaultActive, setHeuristicDefaultActive] = useState<boolean>(
        () =>
            mediaTypeSegments &&
            initialMediaType !== "image" &&
            initialMediaType !== "video" &&
            !selectionHasVideo &&
            !selectionHasImage
    );

    const [activePromotionType, setActivePromotionType] =
        useState<PromotionType | null>(null);

    // Reset transient dialog state every time the modal closes so the next open starts fresh.
    useEffect(() => {
        if (!isOpen) {
            setPendingUploads(0);
            setShowPendingPopup(false);
            setActiveUploads(0);
            setShowNavigationWarning(false);
            setShowTabWarning(false);
            setPendingMediaType(null);
            // The segmented control re-derives in the open-sync effect above,
            // so only the dropdown/tab flows reset here.
            if (!mediaTypeSegments) {
                setActiveResourceType(isImageDisabled || isVideo ? ResourceType.Video : ResourceType.Image);
            }
            setActivePromotionType(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Keep activeResourceType in sync with mediaType
    useEffect(() => {
        if (mediaType === "video") {
            setActiveResourceType(ResourceType.Video);
        } else if (mediaType === "image") {
            setActiveResourceType(ResourceType.Image);
        } else if (mediaType === "assets") {
            // On shared media tab: Video if platform is video-only (e.g. YouTube) or post is video; Image otherwise.
            // The My Brand segment control owns the resource type itself.
            if (!mediaTypeSegments) {
                setActiveResourceType(isImageDisabled || isVideo ? ResourceType.Video : ResourceType.Image);
            }
        }
    }, [mediaType, isVideo, isImageDisabled, mediaTypeSegments]);

    // Limits based on selected media type
    const minMedia = useMemo(() => {
        if (postType === "Promotion") return 1;
        if (mediaType === "video") {
            // Segmented flows keep text posts legal from the video side; the
            // tab flows keep their hard minimum.
            if (mediaTypeSegments) {
                return videoSideRequiresMedia(platforms, limits.map((l) => l.min))
                    ? 1
                    : 0;
            }
            return 1;
        }
        // Video showcase posts always require at least one video, regardless of which tab is active.
        if (isVideo) return 1;
        // Shared media tab with the video side active: a video is only mandatory
        // when a selected platform requires media at all (YouTube requires video,
        // Instagram requires media). A FB / X / LinkedIn-only text post stays
        // legal from the video view, exactly as it is from the image view.
        if (mediaType === "assets" && activeResourceType === ResourceType.Video) {
            if (mediaTypeSegments) {
                return videoSideRequiresMedia(platforms, limits.map((l) => l.min))
                    ? 1
                    : 0;
            }
            return 1;
        }
        return Math.max(...limits.map((l) => l.min));
    }, [mediaType, limits, postType, isVideo, activeResourceType, platforms, mediaTypeSegments]);

    const maxMedia = useMemo(() => {
        if (postType === "Promotion") return 1;
        if (mediaType === "video") return 1;
        return Math.max(...limits.map((l) => l.max));
    }, [mediaType, limits, postType]);

    // Set of shared asset IDs — used by PostMediaSelector to show the Shared badge
    const sharedAssetIds = useMemo(
        () => new Set((media.assets || []).map((a) => a.id)),
        [media.assets]
    );

    // Combine all media into a single list for selection state checks.
    // Must include promotionImages and archived so the maxMedia=1 dedup works across tabs.
    const allMedia: UnifiedMedia[] = useMemo(() => {
        if (!media) return [];
        return [
            ...(media.images || []).map(
                (image) => ({ ...image, mediaType: "image" } as UnifiedMedia)
            ),
            ...(media.videos || []).map(
                (video) => ({ ...video, mediaType: "video" } as UnifiedMedia)
            ),
            ...(media.assets || []).map(
                (asset) => ({
                    ...asset,
                    mediaType: asset.assetType === "Video" ? "video" : "image",
                } as UnifiedMedia)
            ),
            ...((media as any).promotionImages || []).map(
                (img: any) => ({ ...img, mediaType: "image" } as UnifiedMedia)
            ),
            ...((media as any).archived || []).map(
                (img: any) => ({ ...img, mediaType: "image" } as UnifiedMedia)
            ),
        ];
    }, [media]);

    const hasVideoSelected = useMemo(
        () =>
            selectedMediaIds.some((id) => {
                const item = allMedia.find((m) => m.id === id);
                return item?.mediaType === "video";
            }),
        [selectedMediaIds, allMedia]
    );

    const hasImageSelected = useMemo(
        () =>
            selectedMediaIds.some((id) => {
                const item = allMedia.find((m) => m.id === id);
                return item?.mediaType === "image";
            }),
        [selectedMediaIds, allMedia]
    );

    const handleMediaSelect = (id: string) => {
        const item = allMedia.find((m) => m.id === id);
        const itemType = item?.mediaType;
        const isCurrentlySelected = selectedMediaIds.includes(id);

        // Guard against cross-type selection (e.g. picking a video from Shared Media while
        // image-only platforms or an image lock is active). De-selection is always allowed.
        if (!isCurrentlySelected) {
            if (itemType === "video" && (isVideoDisabled || flexLock === "image")) {
                toast.error(
                    <CustomToast
                        message={
                            isVideoDisabled
                                ? "Selected platforms don't support video."
                                : "Remove the selected images before adding a video."
                        }
                        type="error"
                    />,
                    { autoClose: 3000, toastId: "media-lock-video-pick" }
                );
                return;
            }
            if (itemType === "image" && (isImageDisabled || flexLock === "video")) {
                toast.error(
                    <CustomToast
                        message={
                            isImageDisabled
                                ? "Selected platforms don't support images."
                                : "Remove the selected video before adding images."
                        }
                        type="error"
                    />,
                    { autoClose: 3000, toastId: "media-lock-image-pick" }
                );
                return;
            }
        }

        setSelectedMediaIds((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((mediaId) => mediaId !== id);
            }
            // Video is always capped at 1 regardless of platform — replace any existing video.
            if (itemType === "video") {
                const withoutVideos = prevSelected.filter((existingId) => {
                    const existing = allMedia.find((m) => m.id === existingId);
                    return existing?.mediaType !== "video";
                });
                return [...withoutVideos, id];
            }
            // Images: platforms with a single-image limit (Pinterest/GMB) replace prior images,
            // otherwise append.
            if (maxMedia === 1) {
                const withoutImages = prevSelected.filter((existingId) => {
                    const existing = allMedia.find((m) => m.id === existingId);
                    return existing?.mediaType !== "image";
                });
                return [...withoutImages, id];
            }
            return [...prevSelected, id];
        });
    };

    // Derived activeTab for PostMediaSelector
    const activeTab = useMemo(() => {
        if (mediaType === "image") return "images" as const;
        if (mediaType === "video") return "videos" as const;
        if (mediaType === "assets") return "assets" as const;
        if (mediaType === "promotionImages") return "promotionImages" as const;
        if (mediaType === "archived") return "archived" as const;
        return "assets" as const;
    }, [mediaType]);

    // PostMediaSelector data props per mediaType.
    const selectorDataProps = useMemo(() => {
        switch (mediaType) {
            case "image":
                return {
                    images: media.images,
                    assets: media.assets,
                    type: (uploadsEnabled ? "image" : "assetImage") as MediaType,
                };
            case "video":
                return {
                    videos: media.videos,
                    assets: media.assets,
                    type: (uploadsEnabled ? "video" : "assetVideo") as MediaType,
                };
            case "assets":
                return {
                    assets: media.assets,
                    type: "asset" as MediaType,
                };
            case "promotionImages":
                return {
                    promotionImages: (media as any).promotionImages ?? [],
                    type: "image" as MediaType,
                };
            case "archived":
                return {
                    archived: (media as any).archived ?? [],
                    type: "image" as MediaType,
                };
        }
    }, [mediaType, media, uploadsEnabled]);

    // Soft lock for flex platforms: the user's first pick locks out the other media type.
    const flexLock = useMemo<"image" | "video" | null>(() => {
        if (postType === "Promotion") return null;
        if (isImageDisabled || isVideoDisabled) return null; // hard constraint already handles it
        if (hasVideoSelected) return "video";
        if (hasImageSelected) return "image";
        return null;
    }, [postType, isImageDisabled, isVideoDisabled, hasVideoSelected, hasImageSelected]);

    // Final per-tab disabled flags that combine hard platform constraints with the flex lock.
    const imageTabDisabled = isImageDisabled || flexLock === "video";
    const videoTabDisabled = isVideoDisabled || flexLock === "image";

    // ── Segmented Images / Video control (My Brand + listing ad-hoc) ──
    const sharedSegment: SharedMediaSegment =
        mediaType === "video" || activeResourceType === ResourceType.Video
            ? "video"
            : "images";

    const sharedLocks = useMemo(
        () =>
            sharedSegmentLocks({
                platforms,
                isVideo,
                hasVideoSelected,
                hasImageSelected,
            }),
        [platforms, isVideo, hasVideoSelected, hasImageSelected]
    );

    // Any segmented switch that changes mediaType remounts the uploader (its
    // key is the selector type) and would kill in-flight uploads - defer it
    // behind the same warning the tab flow uses.
    const requestSegmentedApply = (
        targetMediaType: MediaDropdownType,
        apply: () => void
    ) => {
        if (activeUploads > 0 && targetMediaType !== mediaType) {
            pendingSegmentApplyRef.current = apply;
            setShowTabWarning(true);
            return;
        }
        apply();
    };

    const handleSegmentClick = (segment: SharedMediaSegment) => {
        setHeuristicDefaultActive(false);
        userDroveSegmentsRef.current = true;
        if (segment === sharedSegment) return;
        const lock = segment === "images" ? sharedLocks.images : sharedLocks.video;
        if (lock) {
            toast.error(
                <CustomToast
                    message={segmentLockMessage(segment, lock, platforms)}
                    type="error"
                />,
                { autoClose: 3000, toastId: `segment-lock-${segment}` }
            );
            return;
        }
        if (sharedMediaOnly) {
            const apply = (): void => {
                setActiveResourceType(
                    segment === "video" ? ResourceType.Video : ResourceType.Image
                );
                onMediaTypeChange?.(segment === "video" ? "video" : "image");
            };
            // Shared-only flows stay on the "assets" mediaType on both sides, so
            // requestSegmentedApply's media-type check can never raise the
            // warning here - gate on the side switch itself.
            if (activeUploads > 0) {
                pendingSegmentApplyRef.current = apply;
                setShowTabWarning(true);
                return;
            }
            apply();
            return;
        }
        const source = defaultSourceFor(segment);
        requestSegmentedApply(source?.mediaType ?? "assets", () => {
            applySource(source, segment, true);
            onMediaTypeChange?.(segment === "video" ? "video" : "image");
        });
    };

    const activeSources = mediaTypeSegments
        ? segmentSources(sharedSegment, sourceInput)
        : [];
    const activeSourceKey =
        mediaType === "image" || mediaType === "video"
            ? "listing"
            : mediaType === "archived"
            ? "archived"
            : activeResourceType === ResourceType.Logo
            ? "logos"
            : "shared";

    // Radix Tabs only reports a change of value, so the re-click on Images that
    // leaves the Logos source rides the trigger's own click - the segment is the
    // escape hatch from an empty filter.
    const handleSegmentReclick = (segment: SharedMediaSegment) => {
        setHeuristicDefaultActive(false);
        userDroveSegmentsRef.current = true;
        if (segment !== "images" || activeResourceType !== ResourceType.Logo) return;
        if (sharedMediaOnly) {
            setActiveResourceType(ResourceType.Image);
            return;
        }
        const source = segmentSources("images", sourceInput)[0] ?? null;
        requestSegmentedApply(source?.mediaType ?? "assets", () =>
            applySource(source, "images", true)
        );
    };

    const sharedCaption = mediaTypeSegments
        ? sharedSegmentCaption({
              locks: sharedLocks,
              platforms,
              segment: sharedSegment,
              heuristicDefaultActive,
          })
        : null;

    // Decide which tabs to surface based on the available media + post type.
    // Image / Video tabs are always rendered (outside Promotion) so the user can see the lock reason.
    const tabConfig = useMemo(() => {
        const tabs: { value: MediaDropdownType; label: string; disabled?: boolean }[] = [];
        // Edit-media flows surface only the Shared Media library to encourage reuse of vetted assets.
        if (sharedMediaOnly) {
            tabs.push({ value: "assets", label: "Shared Media" });
            return tabs;
        }
        if (postType === "Promotion") {
            tabs.push({ value: "promotionImages", label: "Promotional Images" });
            tabs.push({ value: "assets", label: "Shared Media" });
            tabs.push({ value: "archived", label: "Archived Media" });
            return tabs;
        }
        if (!isImageDisabled) {
            tabs.push({ value: "image", label: "Images", disabled: imageTabDisabled });
        }
        if ((media.videos ?? []).length > 0 || isImageDisabled) {
            tabs.push({ value: "video", label: "Videos", disabled: videoTabDisabled });
        }
        if ((media.assets ?? []).length > 0) {
            tabs.push({ value: "assets", label: "Shared Media" });
        }
        if ((media.archived ?? []).length > 0) {
            tabs.push({ value: "archived", label: "Archived Media" });
        }
        return tabs;
    }, [sharedMediaOnly, postType, imageTabDisabled, videoTabDisabled, isImageDisabled, media.videos, media.assets, media.archived]);

    // Media type change with upload-in-progress guard.
    // We preserve selectedMediaIds across tabs; the disabled tab prevents
    // harmful switches and per-item guards catch the rest.
    const handleMediaTypeChange = (newType: MediaDropdownType) => {
        // Block locked tabs and surface why. toastId dedupes rapid repeat clicks.
        if (newType === "image" && imageTabDisabled) {
            toast.error(
                <CustomToast
                    message={
                        isImageDisabled
                            ? "Selected platforms only support video. Deselect video-only platforms to upload images."
                            : "Remove the selected video before switching to images."
                    }
                    type="error"
                />,
                { autoClose: 3000, toastId: "media-lock-image-tab" }
            );
            return;
        }
        if (newType === "video" && videoTabDisabled) {
            toast.error(
                <CustomToast
                    message={
                        isVideoDisabled
                            ? "Selected platforms only support images. Deselect image-only platforms to upload a video."
                            : "Remove the selected images before switching to a video."
                    }
                    type="error"
                />,
                { autoClose: 3000, toastId: "media-lock-video-tab" }
            );
            return;
        }
        if (activeUploads > 0 && mediaType !== newType) {
            setPendingMediaType(newType);
            setShowTabWarning(true);
        } else {
            setMediaType(newType);
            // Segmented flows stamp the wizard only on segment clicks - source
            // browsing must not overwrite the stored choice.
            if (!mediaTypeSegments) {
                onMediaTypeChange?.(newType);
            }
        }
    };

    const confirmMediaTypeChange = () => {
        sessionStorage.removeItem("uploadsInterrupted");
        setActiveUploads(0);
        if (pendingSegmentApplyRef.current) {
            const apply = pendingSegmentApplyRef.current;
            pendingSegmentApplyRef.current = null;
            apply();
        }
        if (pendingMediaType) {
            setMediaType(pendingMediaType);
            if (!mediaTypeSegments) {
                onMediaTypeChange?.(pendingMediaType);
            }
            setPendingMediaType(null);
        }
        setShowTabWarning(false);
    };

    const cancelMediaTypeChange = () => {
        setPendingMediaType(null);
        pendingSegmentApplyRef.current = null;
        setShowTabWarning(false);
    };

    const handlePendingClose = () => {
        setShowPendingPopup(false);
        const uploadBtn = document.getElementById("upload-button");
        if (uploadBtn) {
            uploadBtn.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    const handleActiveUploadsChange = (count: number) => {
        setActiveUploads(count);
    };

    useEffect(() => {
        if (activeUploads === 0 && showTabWarning) {
            setShowTabWarning(false);
            setPendingMediaType(null);
            pendingSegmentApplyRef.current = null;
        }
    }, [activeUploads, showTabWarning]);

    const handleDialogClose = (open: boolean) => {
        if (!open && activeUploads > 0) {
            setShowNavigationWarning(true);
        } else if (!open) {
            onClose();
        }
    };

    const handleLeaveWithUploads = () => {
        sessionStorage.removeItem("uploadsInterrupted");
        setShowNavigationWarning(false);
        setActiveUploads(0);
        onClose();
    };

    const handleStayOnPage = () => {
        setShowNavigationWarning(false);
    };

    return (
        <>
        {isSaving && ReactDOM.createPortal(
            <div style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.3)" }}>
                <Spinner />
            </div>,
            document.body
        )}
        <Dialog open={isOpen} onOpenChange={handleDialogClose}>
            <DialogContent
                className={`max-w-4xl bg-white ${
                    isMobile ? "w-full h-full p-4" : ""
                }`}
                dark={true}
            >
                {showBackConfirm && (
                    <ConfirmPopup
                        open={showBackConfirm}
                        title="Go Back?"
                        message={
                            warnAboutContentEdits
                                ? "Going back will clear your selected media. Any platform-specific content edits made in the final preview step will also be lost."
                                : "Going back will clear your selected media."
                        }
                        confirmText="Go Back"
                        cancelText="Cancel"
                        onConfirm={onConfirmBack!}
                        onCancel={onCancelBack!}
                        confirmVariant="destructive"
                    />
                )}
                <UploadNavigationWarning
                    isOpen={showNavigationWarning}
                    onClose={handleStayOnPage}
                    onLeave={handleLeaveWithUploads}
                    onStay={handleStayOnPage}
                />
                <UploadNavigationWarning
                    isOpen={showTabWarning}
                    onClose={cancelMediaTypeChange}
                    onLeave={confirmMediaTypeChange}
                    onStay={cancelMediaTypeChange}
                />
                <ReconfirmOverlay
                    isOpen={showPendingPopup}
                    onClose={handlePendingClose}
                    onConfirm={handlePendingClose}
                    title="Media Not Uploaded"
                    message={`You have ${pendingUploads} file${
                        pendingUploads > 1 ? "s" : ""
                    } selected but not uploaded.`}
                    messageTwo="Please Upload or remove before continuing."
                    confirmText="Okay"
                    noButtons={true}
                />
                <DialogHeader>
                    <div className="relative flex items-center justify-center">
                        <DialogTitle>Select Media</DialogTitle>
                        <button
                            onClick={onClose}
                            className="absolute right-0 text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </DialogHeader>

                {mediaTypeSegments && (
                    <Tabs
                        value={sharedSegment}
                        onValueChange={(value) =>
                            handleSegmentClick(value as SharedMediaSegment)
                        }
                        className="mt-4"
                    >
                        <TabsList>
                            {(
                                [
                                    { segment: "images", label: "Images", Icon: ImageIcon, lock: sharedLocks.images },
                                    { segment: "video", label: "Video", Icon: VideoIcon, lock: sharedLocks.video },
                                ] as const
                            ).map(({ segment, label, Icon, lock }) => (
                                <TabsTrigger
                                    key={segment}
                                    value={segment}
                                    onClick={() => handleSegmentReclick(segment)}
                                    className={cn("gap-1.5", lock && "cursor-not-allowed opacity-50")}
                                >
                                    <Icon className="h-4 w-4" />
                                    {label}
                                    {lock && <Lock className="h-3 w-3" />}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {sharedCaption && (
                            <p className="mt-2 text-sm text-gray-500">
                                <strong className="font-semibold text-primary">
                                    {sharedCaption.lead}
                                </strong>{" "}
                                - {sharedCaption.rest}
                            </p>
                        )}
                        {activeSources.length > 1 && (
                            <div className="mt-2 flex items-center gap-2">
                                {activeSources.map((source) => (
                                    <button
                                        key={source.key}
                                        type="button"
                                        onClick={() => {
                                            userDroveSegmentsRef.current = true;
                                            requestSegmentedApply(source.mediaType, () =>
                                                applySource(source, sharedSegment, true)
                                            );
                                        }}
                                        className={cn(
                                            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold transition-colors",
                                            activeSourceKey === source.key
                                                ? "border-secondary bg-secondary text-primary"
                                                : "border-gray-300 bg-white text-gray-600 hover:bg-lightSecondary"
                                        )}
                                    >
                                        {source.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </Tabs>
                )}
                <Tabs
                    value={mediaType}
                    onValueChange={(value) =>
                        handleMediaTypeChange(value as MediaDropdownType)
                    }
                    className={mediaTypeSegments ? "mt-2" : "mt-4"}
                >
                    {!mediaTypeSegments && (
                        <TabsList>
                            {tabConfig.map((tab) => (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className={tab.disabled ? "opacity-50" : undefined}
                                >
                                    {tab.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    )}
                    <TabsContent value={mediaType}>
                        <PostMediaSelector
                                {...selectorDataProps}
                                activeTab={activeTab}
                                selectedMedia={selectedMediaIds}
                                setSelectedMedia={setSelectedMediaIds}
                                onSelect={handleMediaSelect}
                                maxMedia={maxMedia}
                                minMedia={minMedia}
                                showAssetsUploader={uploadsEnabled || (postType === "Promotion" && mediaType === "assets")}
                                listingId={listingId}
                                listingType={listingType}
                                onAssetsUploaded={fetchMedia}
                                hasVideoSelected={hasVideoSelected}
                                hasImageSelected={hasImageSelected}
                                setActiveTab={(value) => {
                                    if (mediaTypeSegments) {
                                        // The selector's upload flow emits "assets" so the new
                                        // uploads become visible - follow it to the segment's
                                        // shared source (never stamping the wizard).
                                        // While more uploads are in flight, skip the follow
                                        // rather than queue it - the next batch drain emits
                                        // again with a quiet uploader, and queuing here could
                                        // clobber a user-armed segment switch or pop the
                                        // uploads warning with no user gesture.
                                        if (
                                            value === "assets" &&
                                            mediaType !== "assets" &&
                                            activeUploads === 0
                                        ) {
                                            const shared = segmentSources(sharedSegment, sourceInput).find(
                                                (src) => src.key === "shared"
                                            );
                                            if (shared) {
                                                applySource(shared, sharedSegment, false);
                                            }
                                        }
                                        return;
                                    }
                                    // PostMediaSelector emits its internal tab names; map back to mediaType.
                                    const mapped: MediaDropdownType =
                                        value === "images" ? "image" :
                                        value === "videos" ? "video" :
                                        value === "promotionImages" ? "promotionImages" :
                                        "assets";
                                    handleMediaTypeChange(mapped);
                                }}
                                activeResourceType={activeResourceType}
                                setActiveResourceType={setActiveResourceType}
                                activePromotionType={activePromotionType}
                                setActivePromotionType={setActivePromotionType}
                                uploadsEnabled={uploadsEnabled}
                                platform={platform}
                                postType={postType}
                                isVideo={mediaType === "video" || isVideo}
                                mediaConstraint={
                                    imageTabDisabled
                                        ? "video"
                                        : videoTabDisabled
                                        ? "image"
                                        : undefined
                                }
                                onPendingFilesChange={(count) => setPendingUploads(count)}
                                onActiveUploadsChange={handleActiveUploadsChange}
                                sharedAssetIds={sharedAssetIds}
                                isLoading={isSaving ? false : isLoading}
                                hideTypeControls={mediaTypeSegments}
                        />
                    </TabsContent>
                </Tabs>

                {/* ── Action Buttons ── */}
                <div className="flex justify-between mt-4">
                    <Button variant="secondary" onClick={onClose}>
                        {isNext ? "Back" : "Cancel"}
                    </Button>
                    <Button
                        disabled={isSaving}
                        onClick={() => {
                            if (pendingUploads > 0) {
                                setShowPendingPopup(true);
                                return;
                            }
                            if (selectedMediaIds.length < minMedia) {
                                toast.error(
                                    <CustomToast
                                        message="Please select at least one media"
                                        type="error"
                                    />,
                                    { autoClose: 3000 }
                                );
                            } else if (isVideo && !hasVideoSelected) {
                                toast.error(
                                    <CustomToast
                                        message="Please select a video"
                                        type="error"
                                    />,
                                    { autoClose: 3000 }
                                );
                            } else {
                                onNext();
                            }
                        }}
                    >
                        {isNext ? "Next" : "Save"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default MediaPickerDialog;
